import { useCallback, useMemo, memo } from 'react';
import { FixedSizeList } from 'react-window';
import {
  Checkbox,
  Space,
  Button,
  Tooltip,
  Image,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  CopyOutlined,
  FileImageOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { formatFileSize, formatDate, isImage, getFileUrl } from '../utils/format';
import type { FileRecord } from '../types';

const FileIcon = memo(({ category, colors }: { category: string; colors: any }) => {
  const iconStyle = { fontSize: 20 };
  switch (category) {
    case 'image':
      return <FileImageOutlined style={{ ...iconStyle, color: '#333333' }} />;
    case 'video':
      return <VideoCameraOutlined style={{ ...iconStyle, color: '#555555' }} />;
    case 'document':
      return <FileTextOutlined style={{ ...iconStyle, color: '#777777' }} />;
    default:
      return <FileOutlined style={{ ...iconStyle, color: colors.textMuted }} />;
  }
});

interface VirtualFileListProps {
  files: FileRecord[];
  selectedFiles: string[];
  colors: {
    primary: string;
    text: string;
    textMuted: string;
    border: string;
    background: string;
    hover: string;
  };
  onSelectFile: (fileId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onPreview: (file: FileRecord) => void;
  onCopyUrl: (file: FileRecord) => void;
  onDownload: (file: FileRecord) => void;
  onDelete: (file: FileRecord) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  containerHeight?: number;
}

// 列表项高度
const LIST_ITEM_HEIGHT = 56;

// 单个列表项组件
const ListItem = memo(({
  file,
  isSelected,
  colors,
  onSelect,
  onPreview,
  onCopyUrl,
  onDownload,
  onDelete,
  style,
}: {
  file: FileRecord;
  isSelected: boolean;
  colors: any;
  onSelect: (checked: boolean) => void;
  onPreview: () => void;
  onCopyUrl: () => void;
  onDownload: () => void;
  onDelete: () => void;
  style: React.CSSProperties;
}) => {
  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: isSelected ? colors.hover : 'transparent',
        transition: 'background-color 0.2s',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = colors.hover;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      {/* 选择框 */}
      <div style={{ width: 50, flexShrink: 0 }}>
        <Checkbox checked={isSelected} onChange={(e) => onSelect(e.target.checked)} />
      </div>

      {/* 文件名 */}
      <div style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>
        <Space>
          <FileIcon category={file.fileCategory} colors={colors} />
          <Tooltip title={file.fileName}>
            <span
              style={{
                maxWidth: 300,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                color: colors.text,
                fontSize: 14,
              }}
            >
              {file.fileName}
            </span>
          </Tooltip>
        </Space>
      </div>

      {/* 文件大小 */}
      <div style={{ width: 100, flexShrink: 0 }}>
        <span style={{ color: colors.textMuted, fontSize: 14 }}>
          {formatFileSize(file.fileSize)}
        </span>
      </div>

      {/* 上传时间 */}
      <div style={{ width: 160, flexShrink: 0 }}>
        <span style={{ color: colors.textMuted, fontSize: 14 }}>
          {formatDate(file.createdAt)}
        </span>
      </div>

      {/* 操作按钮 */}
      <div style={{ width: 180, flexShrink: 0 }}>
        <Space size="small">
          <Tooltip title="预览">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={onPreview}
              style={{ color: colors.text }}
            />
          </Tooltip>
          <Tooltip title="复制链接">
            <Button
              type="text"
              icon={<CopyOutlined />}
              onClick={onCopyUrl}
              style={{ color: colors.text }}
            />
          </Tooltip>
          <Tooltip title="下载">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={onDownload}
              style={{ color: colors.text }}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
          </Tooltip>
        </Space>
      </div>
    </div>
  );
});

export default function VirtualFileList({
  files,
  selectedFiles,
  colors,
  onSelectFile,
  onSelectAll,
  onPreview,
  onCopyUrl,
  onDownload,
  onDelete,
  isAllSelected,
  isIndeterminate,
  containerHeight = 500,
}: VirtualFileListProps) {
  // 表头组件
  const header = useMemo(() => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.background,
        fontWeight: 500,
        color: colors.text,
      }}
    >
      <div style={{ width: 50, flexShrink: 0 }}>
        <Checkbox
          checked={isAllSelected}
          indeterminate={isIndeterminate}
          onChange={(e) => onSelectAll(e.target.checked)}
        />
      </div>
      <div style={{ flex: 1, paddingRight: 16 }}>文件名</div>
      <div style={{ width: 100, flexShrink: 0 }}>大小</div>
      <div style={{ width: 160, flexShrink: 0 }}>上传时间</div>
      <div style={{ width: 180, flexShrink: 0 }}>操作</div>
    </div>
  ), [isAllSelected, isIndeterminate, onSelectAll, colors]);

  // 行渲染器
  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const file = files[index];
      // 安全检查：如果 file 不存在，返回空占位
      if (!file) {
        return <div style={style} />;
      }
      const isSelected = selectedFiles.includes(file.id);

      return (
        <ListItem
          file={file}
          isSelected={isSelected}
          colors={colors}
          onSelect={(checked) => onSelectFile(file.id, checked)}
          onPreview={() => onPreview(file)}
          onCopyUrl={() => onCopyUrl(file)}
          onDownload={() => onDownload(file)}
          onDelete={() => onDelete(file)}
          style={style}
        />
      );
    },
    [files, selectedFiles, colors, onSelectFile, onPreview, onCopyUrl, onDownload, onDelete]
  );

  if (files.length === 0) {
    return (
      <div
        style={{
          height: containerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textMuted,
        }}
      >
        暂无文件
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
      {header}
      <FixedSizeList
        height={containerHeight - 50} // 减去表头高度
        itemCount={files.length}
        itemSize={LIST_ITEM_HEIGHT}
        width="100%"
        itemData={{}}
      >
        {/* @ts-ignore */}
        {(props: any) => <Row {...props} />}
      </FixedSizeList>
    </div>
  );
}

// 网格虚拟列表组件
interface VirtualGridFileListProps extends Omit<VirtualFileListProps, 'containerHeight'> {
  containerHeight?: number;
  columnCount?: number;
}

const GRID_ITEM_HEIGHT = 260;

export function VirtualGridFileList({
  files,
  selectedFiles,
  colors,
  onSelectFile,
  onPreview,
  onCopyUrl,
  onDownload,
  onDelete,
  containerHeight = 500,
  columnCount = 4,
}: VirtualGridFileListProps) {
  // 计算行数
  const rowCount = Math.ceil(files.length / columnCount);

  // 行渲染器
  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const startIndex = index * columnCount;
      const rowFiles = files.slice(startIndex, startIndex + columnCount);

      // 安全检查：如果没有文件，返回空占位
      if (rowFiles.length === 0) {
        return <div style={{ ...style, height: GRID_ITEM_HEIGHT }} />;
      }

      return (
        <div
          style={{
            ...style,
            display: 'flex',
            gap: 16,
            padding: '8px',
            boxSizing: 'border-box',
          }}
        >
          {rowFiles.map((file) => {
            const isSelected = selectedFiles.includes(file.id);

            return (
              <div
                key={file.id}
                style={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: `calc(${100 / columnCount}% - 12px)`,
                  height: '100%',
                }}
              >
                <div
                  style={{
                    border: isSelected
                      ? `2px solid ${colors.primary}`
                      : `1px solid ${colors.border}`,
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* 预览区域 */}
                  <div
                    style={{
                      position: 'relative',
                      height: 160,
                      background: colors.background,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                    onClick={() => onPreview(file)}
                  >
                    {isImage(file.fileType) ? (
                      <Image
                        alt={file.fileName}
                        src={getFileUrl(file.fileUrl, file.filePath)}
                        style={{
                          maxHeight: 160,
                          maxWidth: '100%',
                          objectFit: 'contain',
                        }}
                        preview={false}
                      />
                    ) : (
                      <FileIcon category={file.fileCategory} colors={colors} />
                    )}
                    {/* 选择框 */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 10,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => onSelectFile(file.id, e.target.checked)}
                      />
                    </div>
                  </div>

                  {/* 信息区域 */}
                  <div style={{ padding: '8px 12px 10px 12px', display: 'flex', flexDirection: 'column' }}>
                    <Tooltip title={file.fileName} placement="top">
                      <div
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: 14,
                          fontWeight: 500,
                          color: colors.text,
                          marginBottom: 4,
                          lineHeight: '18px',
                        }}
                      >
                        {file.fileName}
                      </div>
                    </Tooltip>
                    <div
                      style={{
                        fontSize: 12,
                        color: colors.textMuted,
                        marginBottom: 6,
                      }}
                    >
                      {formatFileSize(file.fileSize)} · {formatDate(file.createdAt)}
                    </div>
                    {/* 操作按钮 */}
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => onPreview(file)}
                        style={{ color: colors.text }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => onCopyUrl(file)}
                        style={{ color: colors.text }}
                      />
                      <Button
                        type="text"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => onDownload(file)}
                        style={{ color: colors.text }}
                      />
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => onDelete(file)}
                      />
                    </Space>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    },
    [files, selectedFiles, colors, columnCount, onSelectFile, onPreview, onCopyUrl, onDownload, onDelete]
  );

  if (files.length === 0) {
    return (
      <div
        style={{
          height: containerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textMuted,
        }}
      >
        暂无文件
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 0' }}>
      <FixedSizeList
        height={containerHeight}
        itemCount={rowCount}
        itemSize={GRID_ITEM_HEIGHT}
        width="100%"
        itemData={{}}
      >
        {/* @ts-ignore */}
        {(props: any) => <Row {...props} />}
      </FixedSizeList>
    </div>
  );
}
